/*
 * Copyright 2026 Sony Corporation
 */

import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Link, TextField } from '@mui/material';
import { useState } from 'react';

import { connect } from '../services/sketchfab-auth';

export interface SketchfabConnectProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Where the user's Sketchfab API token is pasted.
 *
 * A password field, because it is one in every sense that matters: anyone
 * holding it can act as that account. It is checked against `/v3/me` before it
 * is kept, so a typo is caught here rather than on the first import.
 */
export const SketchfabConnect = ({ open, onClose }: SketchfabConnectProps) => {
  const [token, setToken] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();

  const submit = async () => {
    setBusy(true);
    setError(undefined);
    const result = await connect(token);
    setBusy(false);

    if (result.error) {
      setError(result.error);
      return;
    }
    setToken('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="xs">
      <DialogTitle>Connect Sketchfab</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" className="mb-4">
            {error}
          </Alert>
        )}
        <TextField
          label="API token"
          type="password"
          value={token}
          onChange={(event) => setToken(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && !busy) {
              void submit();
            }
          }}
          autoFocus
          fullWidth
          margin="dense"
          helperText={
            <>
              Find it in your{' '}
              <Link href="https://sketchfab.com/settings/password" target="_blank" rel="noreferrer noopener">
                Sketchfab password settings
              </Link>
              . It stays in this tab and is never sent to Device Verse.
            </>
          }
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button onClick={() => void submit()} variant="contained" disabled={busy || token.trim() === ''}>
          Connect
        </Button>
      </DialogActions>
    </Dialog>
  );
};
